import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { CertificateLevel } from '@estercare/ester-shared'
import { theme } from '../../../theme/theme'
import { BodyLarge, Label, Title3 } from '../../../components/styles/Typography'
import { OnClickButton } from '../../../components/buttons/onclick/OnClickButton'
import { downloadFile } from '../../../utils/downloadFile'

const CERTIFICATE_LOGO_BASE_URL = 'https://ester-public-assets.s3.eu-north-1.amazonaws.com/certificate-logos'

const CERTIFICATE_LEVEL_CONFIG: Partial<Record<CertificateLevel, { levelNumber: number; logoUrl: string }>> = {
  [CertificateLevel.FOUNDATION]: {
    levelNumber: 1,
    logoUrl: `${CERTIFICATE_LOGO_BASE_URL}/Level%3D1+Foundation+2.png`,
  },
  [CertificateLevel.ADVANCED]: {
    levelNumber: 2,
    logoUrl: `${CERTIFICATE_LOGO_BASE_URL}/Level%3D2+Advanced+2.png`,
  },
  [CertificateLevel.EXCELLENCE]: {
    levelNumber: 3,
    logoUrl: `${CERTIFICATE_LOGO_BASE_URL}/Level%3D3+Excellence+1.png`,
  },
}

type Props = {
  level: CertificateLevel
}

export const CertificateLogoCard = ({ level }: Props) => {
  const { t } = useTranslation()
  const config = CERTIFICATE_LEVEL_CONFIG[level]

  if (!config) return null

  const levelKey = level.toLowerCase()

  return (
    <Card>
      <IconWrapper>
        <img src={config.logoUrl} alt="" width={250} height={250} />
      </IconWrapper>
      <CardContent>
        <LevelBadge>{t('admin.certification.levelBadge', { level: config.levelNumber })}</LevelBadge>
        <Title3>{t(`admin.certification.${levelKey}Title`)}</Title3>
        <CardDescription>{t(`admin.certification.${levelKey}Description`)}</CardDescription>
        <OnClickButton text={t('admin.certification.downloadLogo')} onClick={() => downloadFile(config.logoUrl)} />
      </CardContent>
    </Card>
  )
}

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xlarge}px;
  background-color: ${theme.color.beigeLight};
  border-radius: ${theme.spacing.medium}px;
  padding: ${theme.spacing.large}px;
  margin-bottom: ${theme.spacing.xlarge}px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing.medium}px;
  }
`

const IconWrapper = styled.div`
  flex-shrink: 0;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacing.small}px;
`

const LevelBadge = styled(Label)`
  background-color: ${theme.color.plumMid};
  color: ${theme.color.white};
  padding: ${theme.spacing.tiny}px ${theme.spacing.small}px;
  border-radius: ${theme.spacing.tiny}px;
`

const CardDescription = styled(BodyLarge)`
  max-width: 720px;
`
